import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Loader,
  AlertCircle,
  ExternalLink,
  Handshake,
  Pencil,
  Trash2,
  Check,
  Users as UsersIcon,
} from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { partnershipsApiService } from '../../services/partnershipsApi';
import {
  ModpackFull,
  UpdateModpackRequest,
  PLATFORM_TYPES,
  PlatformType,
} from '../../types/partnership';
import { PartnershipStatusBadge } from './PartnershipStatusBadge';
import { PartnershipPriorityBadge } from './PartnershipPriorityBadge';
import { formatDistanceToNow } from 'date-fns';
import { ConfirmationModal } from '../ConfirmationModal';
import { ApogeePackEnrichment } from '../apogee/ApogeePackEnrichment';

interface ModpackDetailModalProps {
  modpackId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

export const ModpackDetailModal = ({
  modpackId,
  isOpen,
  onClose,
  onUpdate,
  canEdit = false,
  canDelete = false,
}: ModpackDetailModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [modpack, setModpack] = useState<ModpackFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPlatformType, setEditPlatformType] = useState<PlatformType | ''>('');
  const [editPlatformUrl, setEditPlatformUrl] = useState('');
  const [editPlatformSlug, setEditPlatformSlug] = useState('');
  const [editLogoUrl, setEditLogoUrl] = useState('');
  const [editPlayerCount, setEditPlayerCount] = useState('');

  useEffect(() => {
    if (!isOpen || !modpackId) return;
    setLoading(true);
    setError(null);

    partnershipsApiService
      .getModpackFull(modpackId)
      .then((data) => {
        setModpack(data);
        setEditName(data.name);
        setEditDescription(data.description || '');
        setEditPlatformType(data.platform_type || '');
        setEditPlatformUrl(data.platform_url || '');
        setEditPlatformSlug(data.platform_slug || '');
        setEditLogoUrl(data.logo_url || '');
        setEditPlayerCount(data.player_count_estimate?.toString() || '');
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load modpack'))
      .finally(() => setLoading(false));
  }, [isOpen, modpackId]);

  const handleSave = async () => {
    if (!modpack) return;
    setIsSaving(true);
    try {
      const data: UpdateModpackRequest = {
        name: editName.trim(),
        description: editDescription.trim() || null,
        platform_type: (editPlatformType as PlatformType) || null,
        platform_url: editPlatformUrl.trim() || null,
        platform_slug: editPlatformSlug.trim() || null,
        logo_url: editLogoUrl.trim() || null,
        player_count_estimate: editPlayerCount ? Number(editPlayerCount) : null,
      };
      await partnershipsApiService.updateModpack(modpack.id, data);
      const updated = await partnershipsApiService.getModpackFull(modpack.id);
      setModpack(updated);
      setIsEditing(false);
      onUpdate?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update modpack');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!modpack) return;
    setIsDeleting(true);
    try {
      await partnershipsApiService.deleteModpack(modpack.id);
      onUpdate?.();
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete modpack');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-2xl">
        <button onClick={onClose} className="absolute top-3 right-3 text-glass-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-6 h-6 animate-spin text-accent-400" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        {modpack && !loading && (
          <div className="space-y-5">
            {/* Header */}
            <div className="flex items-start gap-4">
              {isEditing ? (
                <>
                  {editLogoUrl && (
                    <img
                      src={editLogoUrl}
                      alt="Logo preview"
                      className="w-16 h-16 rounded-xl object-cover bg-dark-900/50 flex-shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <div className="flex-1 space-y-3">
                    <div>
                      <label className="text-xs text-glass-500 mb-1 block">Name</label>
                      <input value={editName} onChange={(e) => setEditName(e.target.value)} className="glass-input w-full text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-glass-500 mb-1 block">Logo URL</label>
                      <input value={editLogoUrl} onChange={(e) => setEditLogoUrl(e.target.value)} className="glass-input w-full text-sm" placeholder="https://..." />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {modpack.logo_url && (
                    <img
                      src={modpack.logo_url}
                      alt={modpack.name}
                      className="w-16 h-16 rounded-xl object-cover bg-dark-900/50 flex-shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold tracking-tight text-white mb-1 truncate">{modpack.name}</h2>
                    {modpack.creator && (
                      <p className="text-sm text-glass-400 flex items-center gap-1">
                        <UsersIcon className="w-3.5 h-3.5" />
                        {modpack.creator.name}
                        <span className="text-glass-500">({modpack.creator.type})</span>
                      </p>
                    )}
                  </div>
                </>
              )}
              <div className="flex items-center gap-1 flex-shrink-0">
                {canEdit && !isEditing && (
                  <button onClick={() => setIsEditing(true)} className="glass-button-secondary text-xs py-1 px-2">
                    <Pencil className="w-3 h-3 mr-1" />Edit
                  </button>
                )}
                {isEditing && (
                  <>
                    <button onClick={handleSave} disabled={isSaving} className="glass-button text-xs py-1 px-2">
                      {isSaving ? <Loader className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                      Save
                    </button>
                    <button onClick={() => setIsEditing(false)} className="glass-button-secondary text-xs py-1 px-2">
                      Cancel
                    </button>
                  </>
                )}
                {canDelete && (
                  <button
                    onClick={() => setIsDeleteOpen(true)}
                    className="glass-button-secondary text-xs py-1 px-2 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Details */}
            {isEditing ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-glass-500 mb-1 block">Description</label>
                  <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} className="glass-input w-full text-sm" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-glass-500 mb-1 block">Platform</label>
                    <select value={editPlatformType} onChange={(e) => setEditPlatformType(e.target.value as PlatformType | '')} className="glass-input w-full text-sm">
                      <option value="">None</option>
                      {PLATFORM_TYPES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-glass-500 mb-1 block">Slug</label>
                    <input value={editPlatformSlug} onChange={(e) => setEditPlatformSlug(e.target.value)} className="glass-input w-full text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-glass-500 mb-1 block">Platform URL</label>
                  <input value={editPlatformUrl} onChange={(e) => setEditPlatformUrl(e.target.value)} className="glass-input w-full text-sm" />
                </div>
                <div>
                  <label className="text-xs text-glass-500 mb-1 block">Est. Player Count</label>
                  <input type="number" min={0} value={editPlayerCount} onChange={(e) => setEditPlayerCount(e.target.value)} className="glass-input w-full text-sm" />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {modpack.description && (
                  <div>
                    <span className="text-xs text-glass-500 block mb-0.5">Description</span>
                    <p className="text-sm text-glass-200">{modpack.description}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-4 text-sm">
                  {modpack.platform_type && (
                    <div>
                      <span className="text-xs text-glass-500 block">Platform</span>
                      <span className="text-glass-200 capitalize">{modpack.platform_type}</span>
                    </div>
                  )}
                  {modpack.platform_slug && (
                    <div>
                      <span className="text-xs text-glass-500 block">Slug</span>
                      <code className="text-glass-200 text-xs font-mono">{modpack.platform_slug}</code>
                    </div>
                  )}
                  {modpack.player_count_estimate && (
                    <div>
                      <span className="text-xs text-glass-500 block">Est. Players</span>
                      <span className="text-glass-200">{modpack.player_count_estimate.toLocaleString()}</span>
                    </div>
                  )}
                </div>
                {modpack.platform_url && (
                  <a
                    href={modpack.platform_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-accent-400 hover:text-accent-300"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View on {modpack.platform_type || 'platform'}
                  </a>
                )}
              </div>
            )}

            {/* Apogee Enrichment — live MPI data */}
            <ApogeePackEnrichment packName={modpack.name} mode="card" />

            {/* Partnerships */}
            <div>
              <h3 className="text-sm font-semibold text-glass-300 mb-2 flex items-center gap-1.5">
                <Handshake className="w-4 h-4" />
                Partnerships ({modpack.partnerships?.length || 0})
              </h3>
              {modpack.partnerships && modpack.partnerships.length > 0 ? (
                <div className="space-y-2">
                  {modpack.partnerships.map((p) => (
                    <div key={p.id} className="p-3 rounded-lg bg-dark-900/40 border border-glass-200/8 flex items-center gap-3">
                      <PartnershipStatusBadge status={p.status} />
                      <PartnershipPriorityBadge priority={p.priority} />
                      <span className="text-sm text-glass-200 flex-1 truncate">{p.summary || 'No summary'}</span>
                      <span className="text-[11px] text-glass-500">
                        {formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-glass-500">No partnerships for this modpack</p>
              )}
            </div>

            {/* Footer */}
            <div className="text-[11px] text-glass-500 pt-3 border-t border-glass-200/10">
              Created {formatDistanceToNow(new Date(modpack.created_at), { addSuffix: true })}
              {' · '}ID: <code className="font-mono">{modpack.id}</code>
            </div>
          </div>
        )}

        {isDeleteOpen && (
          <ConfirmationModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={handleDelete}
            title="Delete Modpack"
            message="Are you sure? This will also delete all partnerships for this modpack (cascade). This cannot be undone."
            confirmText="Delete"
            isLoading={isDeleting}
          />
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
